/**
 * Vasaloppet Mina Sidor
 * Author: Peter Löfås, peter@lofas.se
 */

import React, { Fragment } from 'react'
import FirebaseContext from '../../providers/Firebase/context';
import { Card, CardHeader, CardContent, Theme, withStyles, Button, createStyles, Grid, CardActionArea, TableContainer, Paper, Table, TableHead, TableRow, TableBody, TableCell, Dialog, DialogTitle, DialogActions, LinearProgress, DialogContent, TextField, Select, Typography } from '@material-ui/core';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import { ApiBackend } from '../../providers/apibackend';
import DeleteIcon from '@material-ui/icons/Delete';
import { DiplomaConfig } from '../../model/DiplomaConfig';

const yearOf = (eventKey: string): number | null => {
    const m = eventKey && eventKey.match(/-(\d{4})$/);
    return m ? parseInt(m[1], 10) : null;
};

interface State {
    loading: boolean;
    diplomas: DiplomaConfig[];
    deleteConfirmation: DiplomaConfig;
    showDeleteConfirmation: boolean;
    selectedYear: number | null;
    showCopyDialog: boolean;
    copySourceYear: number | null;
    copyTargetYear: number | null;
}

class ManageDiplomas extends React.Component<RouteComponentProps, State> {
    static contextType = FirebaseContext;
    constructor(props: RouteComponentProps) {
        super(props);
        this.state = {
            loading: true, diplomas: null, deleteConfirmation: null, showDeleteConfirmation: false,
            selectedYear: null, showCopyDialog: false, copySourceYear: null, copyTargetYear: null
        };
    }

    componentDidMount() {
        this.loadDiplomas();
    }

    loadDiplomas = (selectYear?: number) => {
        this.setState({ loading: true });
        let back = new ApiBackend();
        back.listDiplomas().then((diplomas) => {
            diplomas = diplomas || [];
            diplomas.sort((x, y) => {
                if (x.eventKey == y.eventKey) {
                    return x.diplomaProductVariantName < y.diplomaProductVariantName ? -1 : 1;
                }
                return x.eventKey < y.eventKey ? -1 : 1;
            });
            const years = this.distinctYears(diplomas);
            let year = selectYear ?? this.state.selectedYear;
            if (year == null || years.indexOf(year) < 0) {
                const currentYear = new Date().getFullYear();
                year = years.indexOf(currentYear) >= 0 ? currentYear : (years.length ? years[0] : currentYear);
            }
            this.setState({ diplomas: diplomas, selectedYear: year, loading: false });
        });
    }

    distinctYears = (diplomas: DiplomaConfig[]): number[] => {
        const set = new Set<number>();
        (diplomas || []).forEach(d => { const y = yearOf(d.eventKey); if (y != null) set.add(y); });
        return Array.from(set).sort((a, b) => b - a);
    }

    deleteDiploma = (diploma: DiplomaConfig) => () => {
        this.setState({ deleteConfirmation: diploma, showDeleteConfirmation: true });
    }

    render() {
        let { classes } = this.props as any;
        const years = this.distinctYears(this.state.diplomas);
        const shown = (this.state.diplomas || []).filter(d => yearOf(d.eventKey) === this.state.selectedYear);

        return <Fragment><Grid container className={classes.root} spacing={2}>
            <Grid item xs={12}>
                <Card>
                    <CardHeader className={classes.cardHeader} title="Köpbara diplom" />
                    <CardContent>
                        {this.state.loading &&
                            <LinearProgress color="secondary" />
                        }
                        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                            <Grid item>
                                <Typography variant="body2" component="span" style={{ marginRight: 8 }}>År:</Typography>
                                <Select native value={this.state.selectedYear ?? ""}
                                    onChange={(ev) => { this.setState({ selectedYear: +(ev.target.value as string) }); }}>
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    {this.state.selectedYear != null && years.indexOf(this.state.selectedYear) < 0 &&
                                        <option value={this.state.selectedYear}>{this.state.selectedYear}</option>}
                                </Select>
                            </Grid>
                            <Grid item>
                                <Button size="small" variant="outlined" startIcon={<FileCopyIcon />}
                                    disabled={this.state.loading}
                                    onClick={() => this.setState({
                                        showCopyDialog: true,
                                        copySourceYear: this.state.selectedYear,
                                        copyTargetYear: (this.state.selectedYear ?? new Date().getFullYear()) + 1
                                    })}>
                                    Kopiera till nytt år
                                </Button>
                            </Grid>
                        </Grid>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Event</StyledTableCell>
                                        <StyledTableCell>Produkt</StyledTableCell>
                                        <StyledTableCell>Variant</StyledTableCell>
                                        <StyledTableCell>Prisgrupp SWE</StyledTableCell>
                                        <StyledTableCell>Prisgrupp Ej SWE</StyledTableCell>
                                        <StyledTableCell></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shown.map((diploma, idx) => {
                                        return <TableRow key={idx}>
                                            <StyledTableCell>{diploma.eventKey}</StyledTableCell>
                                            <StyledTableCell>{diploma.diplomaProductName}</StyledTableCell>
                                            <StyledTableCell>{diploma.diplomaProductVariantName}</StyledTableCell>
                                            <StyledTableCell>{diploma.diplomaProductVariantPriceGroupName_SE}</StyledTableCell>
                                            <StyledTableCell>{diploma.diplomaProductVariantPriceGroupName_NonSE}</StyledTableCell>
                                            <StyledTableCell align="right">
                                                <DeleteIcon onClick={this.deleteDiploma(diploma)} className={classes.icon} style={{ cursor: 'pointer' }} /></StyledTableCell>
                                        </TableRow>
                                    })}
                                    {!this.state.loading && shown.length === 0 &&
                                        <TableRow><StyledTableCell colSpan={6}>Inga diplom för {this.state.selectedYear}</StyledTableCell></TableRow>}
                                </TableBody>
                            </Table></TableContainer>
                    </CardContent>
                    <CardActionArea component="a">
                        <Button onClick={() => { }}>Lägg till diplom</Button>
                    </CardActionArea>
                </Card>
            </Grid>
        </Grid>
            {this.state && this.state.showDeleteConfirmation && this.renderConfirmationDialog()}
            {this.state && this.state.showCopyDialog && this.renderCopyDialog(years)}
        </Fragment>;
    }

    renderConfirmationDialog() {
        return <Dialog
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="confirmation-dialog-title"
            open={true}
        >
            <DialogTitle id="confirmation-dialog-title">Är du säker på att du vill ta bort diplomet?</DialogTitle>
            <DialogActions>
                <Button variant="contained" autoFocus onClick={this.handleDeleteCancel} color="primary">
                    Avbryt
                </Button>
                <Button variant="contained" onClick={this.handleDeleteOk} color="primary">
                    Ja, ta bort
                </Button>
            </DialogActions>
        </Dialog>;
    }

    renderCopyDialog(years: number[]) {
        return <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
            <DialogTitle>Kopiera diplom från ett år</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                        <Typography variant="body2">Från år (mall)</Typography>
                        <Select native fullWidth value={this.state.copySourceYear ?? ""}
                            onChange={(ev) => this.setState({ copySourceYear: +(ev.target.value as string) })}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </Select>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField type="number" fullWidth label="Till år" InputLabelProps={{ shrink: true }}
                            value={this.state.copyTargetYear ?? ""}
                            onChange={(ev) => this.setState({ copyTargetYear: +ev.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                            Alla diplom för källåret kopieras till målåret. Årtalet i eventKey ersätts automatiskt
                            (t.ex. VL-{this.state.copySourceYear} → VL-{this.state.copyTargetYear}). Produktkopplingarna behålls och kan justeras efteråt.
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" disabled={this.state.loading} onClick={this.handleCopyOk}>Kopiera</Button>
                <Button variant="contained" color="primary" style={{ marginLeft: 10 }} disabled={this.state.loading}
                    onClick={() => this.setState({ showCopyDialog: false })}>Avbryt</Button>
            </DialogActions>
        </Dialog>;
    }

    handleCopyOk = () => {
        const { copySourceYear, copyTargetYear, diplomas } = this.state;
        if (!copySourceYear || !copyTargetYear) { return; }
        if (copySourceYear === copyTargetYear) { alert("Käll- och målår måste skilja sig"); return; }
        const years = this.distinctYears(diplomas);
        if (years.indexOf(copyTargetYear) >= 0 &&
            !window.confirm(`Målåret ${copyTargetYear} har redan diplom. Kopian läggs till utöver de befintliga. Fortsätt?`)) {
            return;
        }
        this.setState({ loading: true });
        const back = new ApiBackend();
        back.copyDiplomaConfigsYear(copySourceYear, copyTargetYear).then((ok) => {
            if (ok) {
                this.setState({ showCopyDialog: false });
                this.loadDiplomas(copyTargetYear);
            } else {
                this.setState({ loading: false });
                alert("Kopieringen misslyckades");
            }
        });
    }

    handleDeleteCancel = () => {
        this.setState({ deleteConfirmation: null, showDeleteConfirmation: false });
    }

    handleDeleteOk = () => {
        this.setState({ loading: true });
        var back = new ApiBackend();
        back.deleteDiploma(this.state.deleteConfirmation).then((res) => {
            if (res) {
                this.setState({ deleteConfirmation: null, showDeleteConfirmation: false });
                this.loadDiplomas();
            }
            else {
                this.setState({ loading: false });
                alert("Kunde inte ta bort diplom!");
            }
        })
    }
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: '30px',
        verticalAlign: 'middle',
        borderRadius: '10px'
    },
    root: {

    },
    form: {
        '& > *': {
            margin: spacing(1),
            width: '25ch',
        },
        '& label.Mui-focused': {
            color: palette.secondary.main,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: palette.secondary.main,
        },
    }
}
);

const tableHeadStyles = ({ palette, spacing }: Theme) => createStyles({
    head: {
        background: palette.primary.main,
        color: palette.primary.contrastText,
    }
});

const StyledTableCell = withStyles(tableHeadStyles)(TableCell);

export default withRouter(withStyles(useStyles)(ManageDiplomas));
